// OrgAdminPanel.js — InvestorQ Internal Admin v2.0
// World-class private market infrastructure
// Visible only to admin users. Review, action, score, and manage org applications.
// Route: activeTab === 'org-admin'
// OffBeet Media & Tech Inc. · investorq.io

import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, Clock, XCircle, FileText, Mail, ChevronDown, ChevronUp,
  RefreshCw, AlertTriangle, Shield, BarChart2, TrendingUp, Users,
  Star, Award, Zap, Eye, Download, Lock, Unlock, Activity
, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';
const ADMIN_EMAILS = ['info@investorq.io', 'sandeep@investorq.io', 'aryan@investorq.io'];

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending_review: { label:'Pending Review',    color:'#94a3b8', bg:'rgba(148,163,184,0.1)', icon:'📋', slaHours: 48 },
  under_review:   { label:'Under Review',       color:'#60a5fa', bg:'rgba(96,165,250,0.1)',  icon:'🔍', slaHours: 72 },
  nda_sent:       { label:'NDA Sent',           color:'#a78bfa', bg:'rgba(167,139,250,0.1)', icon:'🔒', slaHours: 120 },
  docs_requested: { label:'Docs Requested',     color:'#f5a623', bg:'rgba(245,166,35,0.1)',  icon:'📂', slaHours: 240 },
  docs_received:  { label:'Docs Received',      color:'#00d4aa', bg:'rgba(0,212,170,0.1)',   icon:'📂', slaHours: 72  },
  certified:      { label:'Certified',          color:'#00d4aa', bg:'rgba(0,212,170,0.1)',   icon:'🏆', slaHours: null },
  raise_live:     { label:'Raise Live',         color:'#f5a623', bg:'rgba(245,166,35,0.1)',  icon:'🚀', slaHours: null },
  rejected:       { label:'Rejected',           color:'#f87171', bg:'rgba(248,113,113,0.1)', icon:'✗',  slaHours: null },
};

const NEXT_ACTIONS = {
  pending_review: ['under_review', 'rejected'],
  under_review:   ['nda_sent', 'rejected'],
  nda_sent:       ['docs_requested', 'rejected'],
  docs_requested: ['certified', 'rejected'],
  docs_received:  ['certified', 'rejected'],
  certified:      ['raise_live'],
  raise_live:     [],
  rejected:       ['pending_review'],
};

const ACTION_LABELS = {
  under_review:   { label:'Begin Review',       color:'#60a5fa', bg:'rgba(96,165,250,0.15)' },
  nda_sent:       { label:'Send NDA',           color:'#a78bfa', bg:'rgba(167,139,250,0.15)' },
  docs_requested: { label:'Request Documents',  color:'#f5a623', bg:'rgba(245,166,35,0.15)' },
  certified:      { label:'Certify — Tier B',   color:'#00d4aa', bg:'rgba(0,212,170,0.15)' },
  raise_live:     { label:'Mark Raise Live',    color:'#f5a623', bg:'rgba(245,166,35,0.15)' },
  rejected:       { label:'Reject',             color:'#f87171', bg:'rgba(248,113,113,0.15)' },
  pending_review: { label:'Reopen',             color:'#94a3b8', bg:'rgba(148,163,184,0.1)' },
};

const BROKERS = ['DealMaker (sales@dealmaker.tech)', 'Republic (partnerships@republic.com)', 'Wefunder', 'StartEngine', 'Reg A+ direct', 'Other'];

// ─── Cert Score UI ────────────────────────────────────────────────────────────
function CertScoreBar({ score, breakdown }) {
  const color = score >= 70 ? '#00d4aa' : score >= 50 ? '#f5a623' : '#f87171';
  const label = score >= 70 ? 'Strong' : score >= 50 ? 'Moderate' : 'Weak';

  return (
    <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)',
      borderRadius:10, padding:'12px 14px', marginTop:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <span style={{ fontSize:10, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.1em' }}>
          Cert Score
        </span>
        <span style={{ fontSize:14, fontWeight:900, color }}>
          {score}/100 <span style={{ fontSize:9, fontWeight:600 }}>{label}</span>
        </span>
      </div>
      <div style={{ height:5, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden', marginBottom:8 }}>
        <div style={{ width:`${score}%`, height:'100%', background:color,
          borderRadius:3, transition:'width 0.8s ease',
          boxShadow:`0 0 8px ${color}66` }} />
      </div>
      {breakdown && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:4 }}>
          {Object.entries(breakdown).map(([key, val]) => (
            <div key={key} style={{ fontSize:9, color:'#64748b' }}>
              <span style={{ color:'#94a3b8' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</span>: {val}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SLA Badge ────────────────────────────────────────────────────────────────
function SlaBadge({ app }) {
  const sc = STATUS_CONFIG[app.status];
  if (!sc?.slaHours) return null;

  const submittedAt  = new Date(app.reviewed_at || app.submitted_at);
  const hoursPassed  = (Date.now() - submittedAt.getTime()) / 3_600_000;
  const pct          = Math.min(100, (hoursPassed / sc.slaHours) * 100);
  const overdue      = app.slaOverdue  || pct >= 100;
  const warning      = app.slaWarning  || pct >= 75;
  const color        = overdue ? '#f87171' : warning ? '#f5a623' : '#00d4aa';
  const hoursLeft    = Math.max(0, sc.slaHours - hoursPassed);

  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <div style={{ width:48, height:4, background:'rgba(255,255,255,0.06)', borderRadius:2, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:color, borderRadius:2, transition:'width 0.5s' }} />
      </div>
      <span style={{ fontSize:9, fontWeight:800, color,
        padding:'1px 6px', borderRadius:4,
        background:`${color}15`, border:`1px solid ${color}33` }}>
        {overdue ? '⚠ OVERDUE' : `${Math.round(hoursLeft)}h left`}
      </span>
    </div>
  );
}

// ─── Compliance Checklist ─────────────────────────────────────────────────────
function ComplianceChecklist({ app, onUpdate, adminEmail }) {
  const [saving, setSaving] = useState(false);
  const [local, setLocal]   = useState({
    badActorCleared:     app.bad_actor_cleared     || false,
    accreditedConfirmed: app.accredited_confirmed  || false,
    secFormCFiled:       app.sec_form_c_filed       || false,
    complianceNotes:     app.compliance_notes      || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/orgs/admin/${app.id}/compliance`, {
        method: 'PATCH',
        headers: { 'Content-Type':'application/json', 'x-admin-email': adminEmail },
        body: JSON.stringify(local),
      });
      const data = await res.json();
      if (data.success) onUpdate();
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const Flag = ({ field, label, desc, warn }) => (
    <label style={{ display:'flex', gap:10, alignItems:'flex-start', cursor:'pointer',
      padding:'8px 10px', borderRadius:8,
      background: local[field] ? 'rgba(0,212,170,0.05)' : warn ? 'rgba(248,113,113,0.05)' : 'transparent',
      border:`1px solid ${local[field] ? 'rgba(0,212,170,0.2)' : 'rgba(255,255,255,0.06)'}`,
      transition:'all 0.2s' }}>
      <input type="checkbox" checked={local[field]}
        onChange={e => setLocal(l => ({ ...l, [field]: e.target.checked }))}
        style={{ marginTop:2, accentColor:'#00d4aa' }} />
      <div>
        <div style={{ fontSize:11, fontWeight:700, color: local[field] ? '#00d4aa' : '#94a3b8' }}>{label}</div>
        <div style={{ fontSize:9, color:'#475569', lineHeight:1.4 }}>{desc}</div>
      </div>
    </label>
  );

  return (
    <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)',
      borderRadius:12, padding:'14px 16px', marginTop:12 }}>
      <div style={{ fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase',
        letterSpacing:'0.1em', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
        <Shield size={12} /> Compliance Checklist
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
        <Flag field="badActorCleared"
          label="Bad Actor Clearance" warn={app.selected_tier === 'C'}
          desc="Confirm no SEC Rule 506 bad actor disqualification for all covered persons" />
        <Flag field="accreditedConfirmed"
          label="Accredited Investor Confirmation"
          desc="Org has confirmed cap table participants meet SEC accredited investor criteria (if applicable)" />
        <Flag field="secFormCFiled"
          label="SEC Form C Filed (Tier C only)"
          desc="SEC Form C submitted with broker-dealer partner — required for Reg CF raises" />
      </div>
      <textarea
        value={local.complianceNotes}
        onChange={e => setLocal(l => ({ ...l, complianceNotes: e.target.value }))}
        placeholder="Compliance notes (BAM checks, notes on financials quality, red flags...)"
        rows={2}
        style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)',
          borderRadius:8, padding:'8px 10px', color:'#94a3b8', fontSize:11, resize:'none',
          fontFamily:'system-ui, -apple-system, sans-serif', boxSizing:'border-box', marginBottom:8 }} />
      <button onClick={handleSave} disabled={saving} style={{
        padding:'6px 14px', borderRadius:8, border:'1px solid rgba(0,212,170,0.3)',
        background:'rgba(0,212,170,0.1)', color:'#00d4aa', fontSize:10, fontWeight:800,
        cursor:'pointer', letterSpacing:'0.06em', opacity: saving ? 0.6 : 1 }}>
        {saving ? 'Saving…' : 'Save Compliance'}
      </button>
    </div>
  );
}

// ─── Audit Log Viewer ─────────────────────────────────────────────────────────
function AuditLogViewer({ appId, adminEmail }) {
  const [log,     setLog]     = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    fetch(`${API_URL}/orgs/admin/${appId}/audit`, {
      headers: { 'x-admin-email': adminEmail }
    })
      .then(r => r.json())
      .then(data => { setLog(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [appId, adminEmail]);

  const EVENT_ICONS = {
    submitted:            '📝',
    status_change:        '🔄',
    email_sent:           '✉️',
    compliance_update:    '🛡️',
    score_recalculated:   '📊',
    doc_uploaded:         '📄',
    investor_interest:    '💰',
    admin_update:         '✏️',
  };

  return (
    <div style={{ background:'rgba(0,0,0,0.2)', border:'1px solid rgba(255,255,255,0.04)',
      borderRadius:10, padding:'12px 14px', marginTop:12, maxHeight:280, overflowY:'auto' }}>
      <div style={{ fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase',
        letterSpacing:'0.1em', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
        <Activity size={12} /> Audit Trail
      </div>
      {loading && <div style={{ fontSize:11, color:'#475569' }}>Loading…</div>}
      {!loading && log.length === 0 && <div style={{ fontSize:11, color:'#475569' }}>No audit events yet.</div>}
      {log.map((entry, i) => (
        <div key={entry.id || i} style={{ display:'flex', gap:10, padding:'7px 0',
          borderBottom: i < log.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
          <span style={{ fontSize:14, flexShrink:0 }}>{EVENT_ICONS[entry.event_type] || '•'}</span>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:11, color:'#94a3b8', lineHeight:1.4 }}>
              {entry.from_status && entry.to_status && (
                <span style={{ color:'#64748b' }}>{entry.from_status} → <strong style={{ color:'#7c6fff' }}>{entry.to_status}</strong> · </span>
              )}
              {entry.notes || entry.event_type}
            </div>
            <div style={{ fontSize:9, color:'#475569', marginTop:2 }}>
              {entry.performed_by} · {new Date(entry.created_at).toLocaleString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Application Row ──────────────────────────────────────────────────────────
// ─── Document Viewer ──────────────────────────────────────────────────────────
function DocumentViewer({ appId, orgName, adminEmail }) {
  const [docs, setDocs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/orgs/admin/${appId}/docs`, {
      headers: { 'x-admin-email': adminEmail }
    })
      .then(r => r.json())
      .then(data => { setDocs(Array.isArray(data) ? data : data.docs || []); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [appId, adminEmail]);

  if (loading) return <div style={{ padding:'30px', textAlign:'center', color:'#64748b', fontSize:12 }}>⏳ Loading documents…</div>;
  if (error)   return <div style={{ padding:'16px', background:'rgba(248,113,113,0.08)', borderRadius:8, color:'#f87171', fontSize:11 }}>⚠️ {error}</div>;
  if (!docs.length) return (
    <div style={{ padding:'30px', textAlign:'center' }}>
      <div style={{ fontSize:28, opacity:0.3, marginBottom:8 }}>📂</div>
      <div style={{ fontSize:13, color:'#64748b' }}>No documents uploaded yet</div>
      <div style={{ fontSize:11, color:'#475569', marginTop:4 }}>Documents appear after org uploads via their secure link.</div>
    </div>
  );
  return (
    <div style={{ padding:'4px 0' }}>
      <div style={{ fontSize:10, color:'#64748b', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:12 }}>
        {docs.length} Document{docs.length !== 1 ? 's' : ''} uploaded
      </div>
      {docs.map((doc, i) => (
        <div key={doc.id || i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
          background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)',
          borderRadius:10, marginBottom:8 }}>
          <div style={{ fontSize:20 }}>
            {doc.doc_type==='FINANCIALS'?'📊':doc.doc_type==='CAPTABLE'?'📋':doc.doc_type==='ORGDECK'?'🏆':doc.doc_type==='SPONSORDECK'?'📺':'📄'}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#e2e8f0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              {doc.doc_name || doc.r2_key?.split('/').pop() || 'Document'}
            </div>
            <div style={{ fontSize:10, color:'#64748b', marginTop:2 }}>
              {doc.doc_type} · {doc.file_size_bytes ? `${(doc.file_size_bytes/1024).toFixed(1)} KB` : ''} ·{doc.uploaded_at ? ` ${new Date(doc.uploaded_at).toLocaleDateString()}` : ''}
            </div>
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            {doc.nda_required && <span style={{ fontSize:9, fontWeight:800, padding:'2px 8px', borderRadius:4, background:'rgba(124,111,255,0.1)', color:'#7c6fff', border:'1px solid rgba(124,111,255,0.2)' }}>NDA</span>}
            <span style={{ fontSize:9, fontWeight:800, padding:'2px 8px', borderRadius:4,
              background: doc.reviewed ? 'rgba(0,212,170,0.1)' : 'rgba(245,166,35,0.1)',
              color: doc.reviewed ? '#00d4aa' : '#f5a623',
              border: `1px solid ${doc.reviewed ? 'rgba(0,212,170,0.2)' : 'rgba(245,166,35,0.2)'}` }}>
              {doc.reviewed ? '✓ Reviewed' : 'Pending'}
            </span>
            <button onClick={async () => {
              try {
                const res = await fetch(`${API_URL}/orgs/admin/${appId}/docs/${doc.id}/download`, { headers: { 'x-admin-email': adminEmail } });
                const data = await res.json();
                if (data.url) window.open(data.url, '_blank');
                else alert('Error: ' + data.error);
              } catch(err) { alert('Error: ' + err.message); }
            }} style={{ padding:'4px 10px', borderRadius:6, border:'1px solid rgba(0,180,170,0.3)',
              background:'rgba(0,180,170,0.08)', color:'#00b4aa', fontSize:9, fontWeight:800, cursor:'pointer' }}>
              ⬇ VIEW
            </button>
          </div>
        </div>
      ))}
      <div style={{ fontSize:9, color:'#334155', marginTop:8, textAlign:'center' }}>
        Documents stored securely in Cloudflare R2 · Accessible only to authorized admins
      </div>
    </div>
  );
}

function ApplicationRow({ app, onAction, refreshing, adminEmail }) {
  const [expanded, setExpanded]     = useState(false);
  const [activeTab, setActiveTab]   = useState('details'); // details | compliance | audit
  const [notes, setNotes]           = useState(app.admin_notes || '');
  const [broker, setBroker]         = useState(app.broker_partner || '');
  const [raiseUrl, setRaiseUrl]     = useState(app.raise_live_url || '');
  const [actioning, setActioning]   = useState(null);
  const [recalculating, setRecalc]  = useState(false);

  const sc    = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending_review;
  const nexts = NEXT_ACTIONS[app.status] || [];
  const tColor = { A:'#00d4aa', B:'#7c6fff', C:'#f5a623' }[app.selected_tier] || '#94a3b8';
  const certScore = app.cert_score || 0;
  const scoreColor = certScore >= 70 ? '#00d4aa' : certScore >= 50 ? '#f5a623' : '#f87171';

  // Certification gate for Tier B — must have bad_actor_cleared
  const certBlocked = nexts.includes('certified') && !app.bad_actor_cleared && (app.status === 'docs_requested' || app.status === 'docs_received');

  const doAction = async (newStatus) => {
    if (newStatus === 'certified' && certBlocked) {
      alert('⚠️ Cannot certify without confirming bad actor clearance in the Compliance tab.');
      setActiveTab('compliance');
      return;
    }
    setActioning(newStatus);
    try {
      if (newStatus === 'nda_sent') {
        const res = await fetch(`${API_URL}/orgs/admin/${app.id}/send-nda`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
        });
        const data = await res.json();
        if (data.success) {
          alert(`✅ NDA sent to ${app.email}\n\nUpload URL:\n${data.uploadUrl || '(check email)'}`);
        } else { alert(`Error: ${data.error}`); }
        await onAction(app.id, {});
      } else if (newStatus === 'certified') {
        const res = await fetch(`${API_URL}/orgs/admin/${app.id}/certify-tier-b`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
          body: JSON.stringify({ adminNotes: notes }),
        });
        const data = await res.json();
        if (data.success) {
          alert(`✅ ${app.org_name} is now Tier B Certified!\nExpires: ${new Date(data.expiresAt).toLocaleDateString()}`);
        } else { alert(`${data.code === 'COMPLIANCE_GATE' ? '⚠️ ' : 'Error: '}${data.error}`); }
        await onAction(app.id, {});
      } else if (newStatus === 'raise_live') {
        const platform = broker.includes('DealMaker') ? 'DealMaker' : 'Republic';
        const res = await fetch(`${API_URL}/orgs/admin/${app.id}/configure-raise`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
          body: JSON.stringify({ platform, campaignUrl: raiseUrl || null, targetAmount: 500000, adminNotes: notes }),
        });
        const data = await res.json();
        if (data.success) { alert(`🚀 Raise is LIVE via ${platform}!`); }
        else { alert(`Error: ${data.error}`); }
        await onAction(app.id, {});
      } else {
        await onAction(app.id, { status: newStatus, adminNotes: notes, brokerPartner: broker, raiseLiveUrl: raiseUrl });
      }
    } catch (err) {
      alert(`Action failed: ${err.message}`);
    }
    setActioning(null);
  };

  const doDelete = async () => {
    if (!window.confirm(`⚠️ Permanently delete "${app.org_name}"?\nThis removes the tile from the market and cannot be undone.`)) return;
    setActioning('deleting');
    try {
      const res = await fetch(`${API_URL}/orgs/admin/${app.id}/delete`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ "${app.org_name}" deleted from the market.`);
        onAction(app.id, {}); // refresh list
      } else { alert(`Error: ${data.error}`); }
    } catch (err) { alert(`Delete failed: ${err.message}`); }
    setActioning(null);
  };

  const doGenerateReport = async () => {
    if (!window.confirm(`Generate + email valuation report for "${app.org_name}"?\nWill be sent to ${app.email}.`)) return;
    setActioning('generating_report');
    try {
      const res = await fetch(`${API_URL}/orgs/admin/${app.id}/generate-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
      });
      const data = await res.json();
      if (data.success) { alert(`✅ Valuation report will be emailed to ${app.email}`); }
      else { alert(`Error: ${data.error}`); }
    } catch (err) { alert(`Report failed: ${err.message}`); }
    setActioning(null);
  };

  const doRecalc = async () => {
    setRecalc(true);
    try {
      await fetch(`${API_URL}/orgs/admin/${app.id}/score/recalc`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', 'x-admin-email': adminEmail },
      });
    } catch (e) {}
    setRecalc(false);
    onAction(app.id, {}); // refresh
  };

  // Cert expiry warning
  const daysToExpiry = app.certification_expires_at
    ? Math.ceil((new Date(app.certification_expires_at) - Date.now()) / 86_400_000) : null;
  const expiryWarning = daysToExpiry !== null && daysToExpiry <= 60;

  return (
    <div style={{ background:'rgba(15,23,42,0.6)', border:`1px solid ${expanded ? 'rgba(124,111,255,0.2)' : 'rgba(255,255,255,0.06)'}`,
      borderRadius:14, marginBottom:10, overflow:'hidden', transition:'all 0.2s' }}>

      {/* Row header — click to expand */}
      <div onClick={() => setExpanded(e => !e)} style={{
        display:'flex', alignItems:'center', gap:14, padding:'14px 18px',
        cursor:'pointer', flexWrap:'wrap' }}>

        <div style={{ width:36, height:36, borderRadius:10,
          background:sc.bg, border:`1px solid ${sc.color}33`,
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
          {sc.icon}
        </div>

        <div style={{ flex:1, minWidth:120 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#e2e8f0' }}>{app.org_name}</div>
          <div style={{ fontSize:11, color:'#64748b' }}>
            {app.sport} · {app.org_type} · #{app.id}
          </div>
        </div>

        {/* Score indicator */}
        <div style={{ textAlign:'center', minWidth:48 }}>
          <div style={{ fontSize:18, fontWeight:900, color:scoreColor, lineHeight:1 }}>{certScore}</div>
          <div style={{ fontSize:8, color:'#475569', letterSpacing:'0.08em' }}>SCORE</div>
        </div>

        <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ fontSize:10, fontWeight:800, padding:'3px 10px', borderRadius:20,
            color:tColor, background:`${tColor}18`, border:`1px solid ${tColor}33` }}>
            Tier {app.selected_tier}
          </span>
          <span style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20,
            color:sc.color, background:sc.bg, border:`1px solid ${sc.color}33` }}>
            {sc.label}
          </span>
          {expiryWarning && (
            <span style={{ fontSize:9, fontWeight:800, color:'#f87171',
              background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)',
              padding:'2px 8px', borderRadius:12 }}>
              ⚠ Expires {daysToExpiry}d
            </span>
          )}
          {app.investor_interest_count > 0 && (
            <span style={{ fontSize:9, fontWeight:700, color:'#f5a623',
              background:'rgba(245,166,35,0.1)', border:'1px solid rgba(245,166,35,0.2)',
              padding:'2px 8px', borderRadius:12 }}>
              💰 {app.investor_interest_count} interested
            </span>
          )}
          <SlaBadge app={app} />
        </div>

        <div style={{ color:'#475569', flexShrink:0 }}>
          {expanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', padding:'16px 18px' }}>

          {/* Sub-tabs */}
          <div style={{ display:'flex', gap:4, marginBottom:14, borderBottom:'1px solid rgba(255,255,255,0.06)', paddingBottom:10 }}>
            {[
              { id:'details',    label:'Details',    icon:FileText },
              { id:'documents',  label:'Documents',  icon:Upload },
              { id:'compliance', label:'Compliance', icon:Shield },
              { id:'audit',      label:'Audit Log',  icon:Activity },
            ].map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setActiveTab(id)} style={{
                padding:'5px 12px', borderRadius:8, border:'none',
                background: activeTab===id ? 'rgba(124,111,255,0.15)' : 'transparent',
                color: activeTab===id ? '#7c6fff' : '#64748b',
                fontSize:10, fontWeight:800, cursor:'pointer', letterSpacing:'0.06em',
                display:'flex', alignItems:'center', gap:4, transition:'all 0.15s',
              }}>
                <Icon size={10}/> {label}
              </button>
            ))}
          </div>

          {/* ── DETAILS TAB ── */}
          {activeTab === 'details' && (
            <div>
              {/* Key fields grid */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                {[
                  { label:'Contact',     value:`${app.first_name} ${app.last_name} — ${app.email}` },
                  { label:'Revenue Band',value: app.revenue_band },
                  { label:'Financials',  value: app.has_financials },
                  { label:'Seasons',     value: app.seasons },
                  { label:'TV Deal',     value: app.tv_deal },
                  { label:'Raise Range', value: app.raise_range },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background:'rgba(255,255,255,0.02)', borderRadius:8, padding:'8px 10px' }}>
                    <div style={{ fontSize:9, color:'#475569', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:3 }}>{label}</div>
                    <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>{value || '—'}</div>
                  </div>
                ))}
              </div>

              {/* Cert score */}
              <CertScoreBar score={certScore} breakdown={app.cert_score_breakdown} />
              <div style={{ marginTop:6, display:'flex', justifyContent:'flex-end' }}>
                <button onClick={doRecalc} disabled={recalculating} style={{
                  fontSize:9, color:'#64748b', background:'transparent', border:'1px solid rgba(255,255,255,0.08)',
                  borderRadius:6, padding:'3px 10px', cursor:'pointer', opacity: recalculating ? 0.5 : 1 }}>
                  {recalculating ? 'Recalculating…' : '↻ Recalculate Score'}
                </button>
              </div>

              {/* Admin notes */}
              <div style={{ marginTop:12 }}>
                <div style={{ fontSize:10, color:'#64748b', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.08em' }}>Admin Notes</div>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Internal notes visible only to admins..."
                  rows={2} style={{
                    width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)',
                    borderRadius:8, padding:'8px 10px', color:'#94a3b8', fontSize:11, resize:'none',
                    fontFamily:'system-ui, -apple-system, sans-serif', boxSizing:'border-box' }} />
              </div>

              {/* Broker + raise URL (for raise_live transition) */}
              {(nexts.includes('raise_live') || app.status === 'raise_live') && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:10 }}>
                  <div>
                    <div style={{ fontSize:10, color:'#64748b', marginBottom:4 }}>Broker Partner</div>
                    <select value={broker} onChange={e => setBroker(e.target.value)} style={{
                      width:'100%', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)',
                      borderRadius:8, padding:'7px 8px', color:'#94a3b8', fontSize:11 }}>
                      <option value="">Select broker…</option>
                      {BROKERS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:'#64748b', marginBottom:4 }}>Raise Live URL</div>
                    <input type="url" value={raiseUrl} onChange={e => setRaiseUrl(e.target.value)}
                      placeholder="https://republic.com/..." style={{
                        width:'100%', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)',
                        borderRadius:8, padding:'7px 8px', color:'#94a3b8', fontSize:11, boxSizing:'border-box' }} />
                  </div>
                </div>
              )}

              {/* Certification gate warning */}
              {certBlocked && (
                <div style={{ marginTop:10, background:'rgba(248,113,113,0.08)',
                  border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 12px',
                  display:'flex', gap:8, alignItems:'center' }}>
                  <AlertTriangle size={14} color="#f87171" />
                  <span style={{ fontSize:11, color:'#f87171' }}>
                    Complete compliance checklist before certifying — bad actor clearance required.
                  </span>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display:'flex', gap:8, marginTop:14, flexWrap:'wrap' }}>
                {nexts.map(nextStatus => {
                  const cfg   = ACTION_LABELS[nextStatus] || {};
                  const isAct = actioning === nextStatus;
                  const blocked = nextStatus === 'certified' && certBlocked;
                  return (
                    <button key={nextStatus} onClick={() => doAction(nextStatus)}
                      disabled={isAct || refreshing || blocked}
                      style={{
                        padding:'8px 16px', borderRadius:10, border:`1px solid ${cfg.color}44`,
                        background: blocked ? 'rgba(255,255,255,0.03)' : cfg.bg,
                        color: blocked ? '#475569' : cfg.color,
                        fontSize:11, fontWeight:800, cursor: blocked ? 'not-allowed' : 'pointer',
                        letterSpacing:'0.06em', transition:'all 0.15s',
                        opacity: isAct ? 0.6 : blocked ? 0.4 : 1,
                      }}>
                      {isAct ? '…' : cfg.label}
                      {blocked && ' 🔒'}
                    </button>
                  );
                })}
              </div>
              {/* Delete + Regenerate Report buttons */}
              <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid rgba(248,113,113,0.15)', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                <button onClick={doDelete} disabled={actioning === 'deleting'}
                  style={{
                    padding:'6px 14px', borderRadius:8, border:'1px solid rgba(248,113,113,0.3)',
                    background:'rgba(248,113,113,0.06)', color:'#f87171',
                    fontSize:10, fontWeight:800, cursor:'pointer', letterSpacing:'0.08em',
                    opacity: actioning === 'deleting' ? 0.5 : 1,
                  }}>
                  {actioning === 'deleting' ? '…' : '🗑 DELETE ORG'}
                </button>
                <button onClick={doGenerateReport} disabled={actioning === 'generating_report'}
                  style={{
                    padding:'6px 14px', borderRadius:8, border:'1px solid rgba(0,180,170,0.3)',
                    background:'rgba(0,180,170,0.06)', color:'#00b4aa',
                    fontSize:10, fontWeight:800, cursor:'pointer', letterSpacing:'0.08em',
                    opacity: actioning === 'generating_report' ? 0.5 : 1,
                  }}>
                  {actioning === 'generating_report' ? '⏳ Generating…' : '📊 REGENERATE REPORT'}
                </button>
                <span style={{ fontSize:9, color:'#475569' }}>Emails PDF valuation report to org</span>
              </div>
            </div>
          )}

          {/* ── DOCUMENTS TAB ── */}
          {activeTab === 'documents' && (
            <DocumentViewer appId={app.id} orgName={app.org_name} adminEmail={adminEmail} />
          )}

          {/* ── COMPLIANCE TAB ── */}
          {activeTab === 'compliance' && (
            <ComplianceChecklist app={app} onUpdate={() => onAction(app.id, {})} adminEmail={adminEmail} />
          )}

          {/* ── AUDIT LOG TAB ── */}
          {activeTab === 'audit' && (
            <AuditLogViewer appId={app.id} adminEmail={adminEmail} />
          )}

        </div>
      )}
    </div>
  );
}

// ─── Summary Stats ─────────────────────────────────────────────────────────────
function AdminStats({ apps }) {
  const stats = {
    total:      apps.length,
    certified:  apps.filter(a => a.status === 'certified').length,
    raiseLive:  apps.filter(a => a.status === 'raise_live').length,
    pending:    apps.filter(a => ['pending_review','under_review'].includes(a.status)).length,
    overdue:    apps.filter(a => a.slaOverdue).length,
    avgScore:   apps.length ? Math.round(apps.reduce((s,a) => s + (a.cert_score||0), 0) / apps.length) : 0,
  };

  const STAT_CARDS = [
    { label:'Total Applications', value:stats.total, color:'#94a3b8', icon:'📋' },
    { label:'Certified (Tier B)',  value:stats.certified,  color:'#7c6fff', icon:'◈' },
    { label:'Live Raises',         value:stats.raiseLive,  color:'#f5a623', icon:'◆' },
    { label:'Needs Action',        value:stats.pending,    color:'#60a5fa', icon:'🔍' },
    { label:'SLA Overdue',         value:stats.overdue,    color: stats.overdue > 0 ? '#f87171' : '#00d4aa', icon:'⚠' },
    { label:'Avg Cert Score',      value:`${stats.avgScore}/100`, color:'#00d4aa', icon:'★' },
  ];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))',
      gap:10, marginBottom:20 }}>
      {STAT_CARDS.map(({ label, value, color, icon }) => (
        <div key={label} style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)',
          borderRadius:12, padding:'12px 14px' }}>
          <div style={{ fontSize:14, marginBottom:4 }}>{icon}</div>
          <div style={{ fontSize:20, fontWeight:900, color, lineHeight:1 }}>{value}</div>
          <div style={{ fontSize:9, color:'#475569', marginTop:4, textTransform:'uppercase', letterSpacing:'0.08em' }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Admin Panel ─────────────────────────────────────────────────────────
export default function OrgAdminPanel({ currentUserEmail }) {
  const [apps,       setApps]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const [filter,     setFilter]     = useState('all');
  const [sortBy,     setSortBy]     = useState('urgency'); // urgency | score | date
  const [search,     setSearch]     = useState('');
  const [slaReport,  setSlaReport]  = useState(null);

  // Password gate state
  const [pwInput,   setPwInput]   = useState('');
  const [pwError,   setPwError]   = useState('');
  const [pwGranted, setPwGranted] = useState(false); // never persists — must re-enter each page load

  const adminEmail = currentUserEmail || (pwGranted ? 'info@investorq.io' : '');

  // Auth gate
  const isAdmin = ADMIN_EMAILS.includes((adminEmail || '').toLowerCase());

  const loadApps = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/orgs/admin/all`, {
        headers: { 'x-admin-email': adminEmail }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setApps(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      isRefresh ? setRefreshing(false) : setLoading(false);
    }
  }, [adminEmail]);

  useEffect(() => { if (isAdmin) loadApps(); }, [isAdmin, loadApps]);

  const handleAction = async (id, payload) => {
    if (Object.keys(payload).length === 0) {
      // Just refresh
      await loadApps(true);
      return;
    }
    try {
      setRefreshing(true);
      const res = await fetch(`${API_URL}/orgs/admin/${id}/review`, {
        method: 'PATCH',
        headers: { 'Content-Type':'application/json', 'x-admin-email': adminEmail },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Update failed');
      await loadApps(true);
    } catch (err) {
      alert(`Action failed: ${err.message}`);
      setRefreshing(false);
    }
  };

  const loadSlaReport = async () => {
    const res  = await fetch(`${API_URL}/orgs/admin/sla-report`, {
      headers: { 'x-admin-email': adminEmail }
    });
    const data = await res.json();
    setSlaReport(data);
  };

  const handleAdminLogin = () => {
    // Hash check: password is stored as env var on backend, but for frontend gate
    // we use a simple session token approach — backend enforces x-admin-email on every call
    if (pwInput === (process.env.REACT_APP_ADMIN_PASSWORD || 'IQ@Admin2026!')) {
      setPwGranted(true);
      setPwError('');
    } else {
      setPwError('Incorrect password.');
      setPwInput('');
    }
  };

  if (!pwGranted) {
    return (
      <div style={{ padding:40, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:400 }}>
        <div style={{ background:'rgba(30,41,59,0.8)', border:'1px solid rgba(124,111,255,0.2)',
          borderRadius:20, padding:'40px 36px', width:'100%', maxWidth:360, textAlign:'center' }}>
          <div style={{ fontSize:36, marginBottom:16 }}>🔐</div>
          <div style={{ fontSize:18, fontWeight:800, color:'#e2e8f0', marginBottom:4 }}>Admin Access</div>
          <div style={{ fontSize:11, color:'#64748b', marginBottom:28 }}>
            InvestorQ Private Market · Restricted
          </div>
          <input
            type="email"
            value={pwInput.includes('@') ? pwInput : ''}
            onChange={() => {}}
            placeholder="info@investorq.io"
            style={{ width:'100%', boxSizing:'border-box', background:'rgba(15,23,42,0.7)',
              border:'1px solid #334155', borderRadius:8, padding:'10px 14px',
              color:'#e2e8f0', fontSize:13, marginBottom:10, outline:'none' }}
            readOnly
          />
          <input
            type="password"
            value={pwInput}
            onChange={e => setPwInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdminLogin()}
            placeholder="Admin password"
            style={{ width:'100%', boxSizing:'border-box', background:'rgba(15,23,42,0.7)',
              border:`1px solid ${pwError ? '#f87171' : '#334155'}`, borderRadius:8,
              padding:'10px 14px', color:'#e2e8f0', fontSize:13, marginBottom:10, outline:'none' }}
            autoFocus
          />
          {pwError && (
            <div style={{ fontSize:11, color:'#f87171', marginBottom:10 }}>⚠️ {pwError}</div>
          )}
          <button onClick={handleAdminLogin} style={{
            width:'100%', padding:'11px', borderRadius:10, border:'none',
            background:'linear-gradient(135deg, #7c6fff, #9b8fff)',
            color:'#fff', fontSize:13, fontWeight:800, cursor:'pointer'
          }}>
            Sign In →
          </button>
          <div style={{ fontSize:10, color:'#334155', marginTop:16 }}>
            Access restricted to info@investorq.io
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>
        <div style={{ fontSize:40, marginBottom:16, opacity:0.3 }}>🔒</div>
        <div style={{ fontSize:16, fontWeight:700, color:'#f87171', marginBottom:8 }}>Admin Access Required</div>
        <div style={{ fontSize:12, color:'#64748b' }}>
          This panel is restricted to InvestorQ admin accounts.
        </div>
      </div>
    );
  }

  // Filter + sort
  const FILTER_OPTIONS = [
    { key:'all',          label:'All' },
    { key:'pending_review', label:'Pending' },
    { key:'under_review', label:'Under Review' },
    { key:'nda_sent',     label:'NDA Sent' },
    { key:'docs_requested',label:'Docs Requested' },
    { key:'docs_received', label:'Docs Received' },
    { key:'certified',    label:'Certified' },
    { key:'raise_live',   label:'Raise Live' },
    { key:'rejected',     label:'Rejected' },
    { key:'overdue',      label:'⚠ Overdue' },
  ];

  let displayed = filter === 'all'    ? apps
    : filter === 'overdue' ? apps.filter(a => a.slaOverdue)
    : apps.filter(a => a.status === filter);

  if (search) {
    const q = search.toLowerCase();
    displayed = displayed.filter(a =>
      a.org_name.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q) ||
      a.sport?.toLowerCase().includes(q)
    );
  }

  if (sortBy === 'urgency') {
    displayed = [...displayed].sort((a, b) => {
      if (a.slaOverdue && !b.slaOverdue) return -1;
      if (!a.slaOverdue && b.slaOverdue) return 1;
      if (a.slaWarning && !b.slaWarning) return -1;
      return 0;
    });
  } else if (sortBy === 'score') {
    displayed = [...displayed].sort((a, b) => (b.cert_score||0) - (a.cert_score||0));
  } else if (sortBy === 'date') {
    displayed = [...displayed].sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
  }

  return (
    <div style={{ padding:'24px', fontFamily:'system-ui,-apple-system,sans-serif', maxWidth:960, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ margin:0, fontSize:22, fontWeight:900, color:'#e2e8f0', letterSpacing:'-0.02em' }}>
            Org Application Admin
          </h2>
          <div style={{ fontSize:11, color:'#64748b', marginTop:4 }}>
            InvestorQ Private Market · Signed in as {adminEmail}
            <span style={{ marginLeft:12, fontSize:9, color:'#7c6fff',
              background:'rgba(124,111,255,0.08)', border:'1px solid rgba(124,111,255,0.2)',
              borderRadius:4, padding:'2px 8px' }}>
              Manages DB orgs only · Static franchise assets (NFL/NBA/IPL) managed via code
            </span>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={loadSlaReport} style={{
            padding:'8px 14px', borderRadius:10, border:'1px solid rgba(248,113,113,0.3)',
            background:'rgba(248,113,113,0.08)', color:'#f87171', fontSize:10, fontWeight:800,
            cursor:'pointer', letterSpacing:'0.06em' }}>
            SLA Report
          </button>
          <button onClick={() => loadApps(true)} disabled={refreshing} style={{
            padding:'8px 14px', borderRadius:10, border:'1px solid rgba(255,255,255,0.1)',
            background:'rgba(255,255,255,0.04)', color:'#94a3b8', fontSize:10, fontWeight:800,
            cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
            <RefreshCw size={12} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
      </div>

      {/* SLA Report modal-ish */}
      {slaReport && (
        <div style={{ background:'rgba(248,113,113,0.06)', border:'1px solid rgba(248,113,113,0.2)',
          borderRadius:12, padding:'14px 18px', marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <span style={{ fontSize:12, fontWeight:800, color:'#f87171' }}>
              ⚠ SLA Report — {slaReport.count} application{slaReport.count !== 1 ? 's' : ''} flagged
            </span>
            <button onClick={() => setSlaReport(null)} style={{ background:'transparent', border:'none',
              color:'#64748b', cursor:'pointer', fontSize:14 }}>✕</button>
          </div>
          {slaReport.items.map(item => (
            <div key={item.id} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0',
              borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:11 }}>
              <span style={{ color:'#94a3b8' }}>{item.org_name}</span>
              <span style={{ color: item.urgency === 'overdue' ? '#f87171' : '#f5a623', fontWeight:700 }}>
                {item.status} · {item.urgency.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {apps.length > 0 && <AdminStats apps={apps} />}

      {/* Filter + search row */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search org, email, sport…"
          style={{ flex:1, minWidth:160, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)',
            borderRadius:8, padding:'7px 10px', color:'#94a3b8', fontSize:11 }} />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
          background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)',
          borderRadius:8, padding:'7px 8px', color:'#94a3b8', fontSize:11 }}>
          <option value="urgency">Sort: Urgency</option>
          <option value="score">Sort: Cert Score</option>
          <option value="date">Sort: Date</option>
        </select>
        <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
          {FILTER_OPTIONS.map(({ key, label }) => {
            const cnt = key === 'all'     ? apps.length
              : key === 'overdue'         ? apps.filter(a => a.slaOverdue).length
              : apps.filter(a => a.status === key).length;
            if (cnt === 0 && key !== 'all') return null;
            return (
              <button key={key} onClick={() => setFilter(key)} style={{
                padding:'5px 10px', borderRadius:8,
                border:`1px solid ${filter===key ? 'rgba(124,111,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                background: filter===key ? 'rgba(124,111,255,0.12)' : 'rgba(255,255,255,0.02)',
                color: filter===key ? '#7c6fff' : '#64748b',
                fontSize:10, fontWeight:800, cursor:'pointer', transition:'all 0.15s' }}>
                {label} {cnt > 0 && <span style={{ opacity:0.6 }}>({cnt})</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {loading && (
        <div style={{ textAlign:'center', padding:'40px', color:'#64748b', fontSize:12 }}>
          Loading applications…
        </div>
      )}
      {error && (
        <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)',
          borderRadius:10, padding:'14px 16px', color:'#f87171', fontSize:12 }}>
          Error: {error}
        </div>
      )}
      {!loading && !error && displayed.length === 0 && (
        <div style={{ textAlign:'center', padding:40, color:'#64748b', fontSize:12 }}>
          {search ? `No results for "${search}"` : 'No applications in this filter.'}
        </div>
      )}
      {!loading && displayed.map(app => (
        <ApplicationRow
          key={app.id}
          app={app}
          onAction={handleAction}
          refreshing={refreshing}
          adminEmail={adminEmail}
        />
      ))}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        select option { background: #1e293b; color: #94a3b8; }
      `}</style>
    </div>
  );
}